import {useEffect, useRef} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Progress, Table, Tag} from 'antd';
import moment from 'moment';
import {GET_BULK_SWAP_JOB, GET_MY_BULK_SWAP_JOBS} from 'graphql/queries/wallet';
import {JOB_STATUS, isJobTerminal, jobRequester} from './swapReporting';
import styles from './swapTransactions.module.scss';

// Reading is only for display: the backend runs the job whether or not anyone
// is watching, so this is free to stop the moment the dialog closes.
const POLL_MS = 3000;
const when = value => (value ? moment(value).format('D MMM YYYY, HH:mm') : '—');

export const JobStatusTag = ({status}) => {
  const meta = JOB_STATUS[status] || {label: status, color: 'default'};
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

/**
 * One bulk job, live. Starts from `initialJob` (the submission receipt, if
 * there is one) and re-reads the job every few seconds until it finishes.
 * `onTerminal` fires once, when a job seen running is seen finished — not for
 * a job that was already finished when it was opened.
 */
export const SwapBulkJobDetails = ({jobId, initialJob, onTerminal}) => {
  const {data, error, startPolling, stopPolling} = useQuery(GET_BULK_SWAP_JOB, {
    variables: {jobId},
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });
  const read = data?.getBulkSwapTransactionJob;
  const job = read?.__typename === 'BulkSwapTransactionJob' ? read : initialJob;
  const readError =
    read?.__typename === 'Error'
      ? read.message
      : error
      ? 'Could not load this job.'
      : null;
  const terminal = isJobTerminal(job);

  useEffect(() => {
    if (terminal) stopPolling();
    else startPolling(POLL_MS);
    return stopPolling;
  }, [terminal, startPolling, stopPolling]);

  const sawRunning = useRef(false);
  useEffect(() => {
    if (!job) return;
    if (!terminal) sawRunning.current = true;
    else if (sawRunning.current) {
      sawRunning.current = false;
      onTerminal?.(job);
    }
  }, [job, terminal, onTerminal]);

  if (!job) {
    return readError ? (
      <Alert type="error" showIcon message={readError} />
    ) : (
      <p className={styles.bulkScope}>Loading the job…</p>
    );
  }

  const percent = job.totalMatched
    ? Math.min(100, Math.round((job.attempted / job.totalMatched) * 100))
    : terminal
    ? 100
    : 0;
  const counts = [
    ['Matched', job.totalMatched],
    ['Processed', job.attempted],
    ['Succeeded', job.succeeded],
    ['Failed', job.failedCount],
    ['Skipped', job.skipped],
  ];

  return (
    <div className={styles.jobDetails}>
      <div className={styles.jobHead}>
        <JobStatusTag status={job.status} />
        <span className={styles.jobMeta}>
          Started by {jobRequester(job)} · {when(job.createdAt)}
        </span>
      </div>

      <Progress
        percent={percent}
        status={
          !terminal
            ? 'active'
            : job.status === 'COMPLETED_WITH_ERRORS'
            ? 'exception'
            : 'success'
        }
        strokeColor={terminal ? undefined : '#8d6cfa'}
      />

      <dl className={styles.jobCounts}>
        {counts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{(value ?? 0).toLocaleString('en-US')}</dd>
          </div>
        ))}
      </dl>

      {!terminal && (
        <Alert
          type="info"
          showIcon
          message={
            job.status === 'QUEUED'
              ? 'Queued. The backend will start it shortly.'
              : 'Running in the background.'
          }
          description="You can close this or leave the page; the job keeps going. Find it again under Recent bulk jobs."
        />
      )}

      {job.skipped > 0 && (
        <p className={styles.jobNote}>
          Skipped swaps were pending when the job started but no longer eligible
          when it reached them.
        </p>
      )}

      {job.failedCount > 0 && (
        <div className={styles.bulkFailures}>
          <strong>
            Not processed ({job.failedCount.toLocaleString('en-US')})
            {job.failed.length < job.failedCount
              ? ` — showing the latest ${job.failed.length}`
              : ''}
          </strong>
          <ul>
            {job.failed.map(item => (
              <li key={item.id}>
                <code>{item.id}</code> — {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {readError && <Alert type="warning" showIcon message={readError} />}
      <span className={styles.jobMeta}>
        Job {job.jobId}
        {job.completedAt ? ` · finished ${when(job.completedAt)}` : ''}
      </span>
    </div>
  );
};

/**
 * The signed-in admin's recent bulk jobs, so a job started and then left can
 * be found again — and checked before anything is resubmitted.
 */
export const SwapBulkJobsList = ({onOpen}) => {
  const {data, loading, error, refetch} = useQuery(GET_MY_BULK_SWAP_JOBS, {
    variables: {limit: 20},
    fetchPolicy: 'network-only',
  });
  const jobs = data?.getMyBulkSwapTransactionJobs || [];

  return (
    <div className={styles.jobList}>
      {error && (
        <Alert type="error" showIcon message="Could not load your bulk jobs." />
      )}
      <Table
        rowKey="jobId"
        size="small"
        loading={loading}
        dataSource={jobs}
        pagination={false}
        scroll={{x: true}}
        locale={{emptyText: 'You have not started any bulk jobs yet.'}}
        columns={[
          {
            title: 'Started',
            key: 'createdAt',
            render: (_, j) => when(j.createdAt),
          },
          {
            title: 'Status',
            key: 'status',
            render: (_, j) => <JobStatusTag status={j.status} />,
          },
          {
            title: 'Progress',
            key: 'progress',
            render: (_, j) =>
              `${j.attempted.toLocaleString(
                'en-US',
              )} / ${j.totalMatched.toLocaleString('en-US')}`,
          },
          {
            title: 'Result',
            key: 'result',
            render: (_, j) =>
              `${j.succeeded} done · ${j.failedCount} failed · ${j.skipped} skipped`,
          },
          {
            title: '',
            key: 'open',
            render: (_, j) => (
              <Button type="link" onClick={() => onOpen(j)}>
                View
              </Button>
            ),
          },
        ]}
      />
      <Button onClick={() => refetch()}>Refresh</Button>
    </div>
  );
};
